import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';

const SESSION_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes

/**
 * Monitors user activity and logs out after 20 minutes of inactivity.
 * Activity events (mouse, keyboard, clicks, scrolls) reset the timer.
 */
export function useSessionTimeout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSessionExpired = useCallback(() => {
    auth.logout();
    navigate('/login?reason=session_expired', { replace: true });
  }, [auth, navigate]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(handleSessionExpired, SESSION_TIMEOUT_MS);
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Activity events that reset the inactivity timer
    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => resetTimer();

    // Start the timer
    resetTimer();

    // Listen for user activity
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [auth.isAuthenticated, resetTimer]);
}
